/**
 * Declarative permission gate that conditionally renders children based on
 * the authenticated user's effective permissions.
 *
 * Usage:
 *   <PermissionGate permission="workspace.agents.manage">
 *     <AgentSettingsGeneral />
 *   </PermissionGate>
 *
 * When the user lacks the required permission, either a fallback component
 * is rendered (if provided) or a default "Access Denied" message is shown.
 *
 * While permissions are still loading, a centered spinner is shown to
 * prevent layout flash.
 *
 * @module components/shared/PermissionGate
 * @see TASK-HLD-PERMISSION-GATES
 */
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LockIcon from "@mui/icons-material/Lock";
import { Trans } from "@lingui/react/macro";
import { usePermissionContext } from "../../store/PermissionContext";

export interface PermissionGateProps {
  /** The permission key required to view children (e.g. "workspace.agents.manage"). */
  permission: string;
  /** Content to render when the user holds the required permission. */
  children: React.ReactNode;
  /** Optional custom fallback when access is denied. Defaults to a generic message. */
  fallback?: React.ReactNode;
  /**
   * When true, the gate renders nothing instead of a fallback when the
   * permission is missing. Useful for hiding sidebar items or optional UI.
   */
  hideOnDenied?: boolean;
}

/**
 * Renders children only when the current user holds the specified permission.
 */
export function PermissionGate({
  permission,
  children,
  fallback,
  hideOnDenied = false,
}: PermissionGateProps): React.ReactElement | null {
  const { hasPermission, loading } = usePermissionContext();

  // Show spinner while permissions are loading to avoid premature denial
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "30vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  // When hideOnDenied is set, render nothing (useful for sidebar items)
  if (hideOnDenied) {
    return null;
  }

  // Custom fallback
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  // Default access-denied UI
  return (
    <Box sx={{ textAlign: "center", py: 8 }}>
      <LockIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
        <Trans>Access Denied</Trans>
      </Typography>
      <Typography variant="body2" color="text.disabled">
        <Trans>
          You do not have permission to view this page. Contact your workspace
          administrator for access.
        </Trans>
      </Typography>
    </Box>
  );
}

export default PermissionGate;
